import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { storage } from '../utils/storage';
import { db } from '../config/firebase';
import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  doc, 
  getDoc, 
  orderBy,
  serverTimestamp,
  updateDoc,
  Timestamp,
  getDocs,
  addDoc
} from 'firebase/firestore';
import { walletService } from '../services/walletService';
import { fetchWithAuth } from '../services/authToken';
import { format } from 'date-fns';

interface Chamado {
  id: string;
  status: string;
  cliente?: string;
  setor?: string;
  frete?: number;
  motoboyId?: string;
  motoboyNome?: string;
  criadoEm?: any;
  empresaId?: string;
}

interface Estatisticas {
  pendente: number;
  entregue: number;
  total: number;
  totalGasto: string;
}

const EmpresaHome: React.FC = () => {
  const navigate = useNavigate();
  const { userData, logout, userId } = useAuth();
  const [chamados, setChamados] = useState<Chamado[]>([]);
  const [empresaNome, setEmpresaNome] = useState<string>('');
  const [empresaId, setEmpresaId] = useState('');
  const [empresaFoto, setEmpresaFoto] = useState<string>('');
  const [filtro, setFiltro] = useState<'todos' | 'pendente' | 'entregue'>('todos');
  const [estatisticas, setEstatisticas] = useState<Estatisticas>({ 
    pendente: 0, 
    entregue: 0, 
    total: 0, 
    totalGasto: '0.00' 
  });
  const [isLoading, setIsLoading] = useState(true);
  const [saldoCarteira, setSaldoCarteira] = useState(0);
  const [saldoVisivel, setSaldoVisivel] = useState(true);
  const [showAdicionarSaldo, setShowAdicionarSaldo] = useState(false);
  const [valorAdicionar, setValorAdicionar] = useState('');
  const [isLoadingSaldo, setIsLoadingSaldo] = useState(false);
  const [usaCarteira, setUsaCarteira] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState<{[key: string]: number}>({});
  const [mensagensNaoLidas, setMensagensNaoLidas] = useState(0);
  const [mensagensCentral, setMensagensCentral] = useState<any[]>([]);
  const [showMensagensModal, setShowMensagensModal] = useState(false);
  const [solicitacoesPendentes, setSolicitacoesPendentes] = useState(0);
  const [statusCadastro, setStatusCadastro] = useState<'pendente' | 'aprovado' | 'rejeitado' | 'desconhecido'>('desconhecido');
  const [mostrarBloqueio, setMostrarBloqueio] = useState(false);
  const [showChamadoPanel, setShowChamadoPanel] = useState(false);
  const [tipoChamado, setTipoChamado] = useState<'detalhado' | 'simplificado' | null>(null);

  // Carregar dados iniciais
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const id = await storage.getItem('userId');
        if (!id) {
          navigate('/login');
          return;
        }

        const userDoc = await getDoc(doc(db, 'usuarios', id));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const status = (userData.status as any) || 'pendente';
          setStatusCadastro(status);
          setMostrarBloqueio(status !== 'aprovado');
          
          if (userData.empresaId) {
            setEmpresaId(userData.empresaId);
            const empresaDoc = await getDoc(doc(db, 'Empresa', userData.empresaId));
            if (empresaDoc.exists()) {
              const empresaData = empresaDoc.data();
              setEmpresaNome(empresaData.nomeEmpresa || 'Empresa');
              setEmpresaFoto(empresaData.foto || empresaData.fotoUrl || '');
              setUsaCarteira(empresaData.usaCarteira === true);
              
              if (empresaData.usaCarteira) {
                const saldoDisponivel = await walletService.getAvailableBalance(userData.empresaId);
                setSaldoCarteira(saldoDisponivel);
              }
            }
          }
        }
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  // Listener para chamados em tempo real
  useEffect(() => {
    if (!empresaId) return;

    const q = query(
      collection(db, 'chamados1'),
      where('empresaId', '==', empresaId),
      orderBy('criadoEm', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      })) as Chamado[];

      setChamados(data);
    });

    return () => unsubscribe();
  }, [empresaId]);

  // Listener para entregues em tempo real - sincronizar estatísticas
  useEffect(() => {
    if (!empresaId) return;

    const empresaRef = doc(db, 'Empresa', empresaId);
    const entreguesRef = collection(empresaRef, 'entregues');
    const qEntregues = query(entreguesRef, orderBy('entregueEm', 'desc'));

    const unsubscribe = onSnapshot(qEntregues, (snapshot) => {
      const entregues = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      })) as any[];

      // Calcular estatísticas baseadas na coleção entregues
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      const fimHoje = new Date(hoje);
      fimHoje.setHours(23, 59, 59, 999);

      let entregue = 0;
      let totalGastoHoje = 0;

      // Contar entregues de hoje e calcular total gasto de hoje
      entregues.forEach((entrega: any) => {
        const dataEntrega = entrega.entregueEm?.toDate ? entrega.entregueEm.toDate() : null;
        if (dataEntrega && dataEntrega >= hoje && dataEntrega <= fimHoje) {
          entregue++;
          const valor = parseFloat(String(entrega.frete || entrega.valor || 0));
          totalGastoHoje += valor;
        }
      });

      // Atualizar estatísticas (mantendo pendente do estado anterior)
      setEstatisticas(prev => ({
        ...prev,
        entregue,
        total: entregue, // Total de entregues de hoje
        totalGasto: totalGastoHoje.toFixed(2) // Total gasto apenas de hoje
      }));
    }, (error) => {
      console.error('Erro ao sincronizar entregues:', error);
    });

    return () => unsubscribe();
  }, [empresaId]);

  // Atualizar pendentes quando chamados mudarem
  useEffect(() => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    const chamadosDoDia = chamados.filter(item => {
      const dataChamado = item.criadoEm?.toDate ? item.criadoEm.toDate() : new Date();
      return dataChamado >= hoje;
    });

    let pendente = 0;
    chamadosDoDia.forEach(item => {
      // Incluir pendente e agendado como pendentes (mesma lógica do StatusPedido)
      if (item.status === 'pendente' || item.status === 'agendado') {
        pendente++;
      }
    });

    setEstatisticas(prev => ({
      ...prev,
      pendente
    }));
  }, [chamados]);

  // Listener para mensagens da central
  useEffect(() => {
    if (!userId) return;

    const unsubscribe = onSnapshot(
      query(
        collection(db, 'usuarios', userId, 'mensagens_central'),
        orderBy('criadoEm', 'desc')
      ),
      (snapshot) => {
        const mensagens = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setMensagensCentral(mensagens);
        const naoLidas = mensagens.filter((m: any) => !m.lida && !m.fechada).length;
        setMensagensNaoLidas(naoLidas);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  // Listener para solicitações pendentes
  useEffect(() => {
    if (!empresaId) return;

    const unsubscribe = onSnapshot(
      query(
        collection(db, 'solicitacoesLancamento'),
        where('empresaId', '==', empresaId),
        where('status', '==', 'pendente')
      ),
      (snapshot) => {
        setSolicitacoesPendentes(snapshot.docs.length);
      }
    );

    return () => unsubscribe();
  }, [empresaId]);


  // Handle refresh
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      if (empresaId && usaCarteira) {
        const saldoDisponivel = await walletService.getAvailableBalance(empresaId);
        setSaldoCarteira(saldoDisponivel);
      }
      // As estatísticas são atualizadas automaticamente pelo listener em tempo real
    } catch (error) {
      console.error('Erro ao atualizar:', error);
    } finally {
      setRefreshing(false);
    }
  }, [empresaId, usaCarteira]);

  // Adicionar saldo
  const handleAdicionarSaldo = async () => {
    if (!valorAdicionar || parseFloat(valorAdicionar) <= 0) {
      alert('Digite um valor válido');
      return;
    }

    setIsLoadingSaldo(true);
    try {
      // Aqui você implementaria a lógica de pagamento
      // Por enquanto, apenas fechamos o modal
      alert('Funcionalidade de adicionar saldo será implementada em breve');
      setShowAdicionarSaldo(false);
      setValorAdicionar('');
    } catch (error) {
      console.error('Erro ao adicionar saldo:', error);
      alert('Erro ao adicionar saldo');
    } finally {
      setIsLoadingSaldo(false);
    }
  };

  // Formatar valor
  const formatarValor = (valor: number) => {
    return valor.toFixed(2).replace('.', ',');
  };

  // Handle logout
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <>
      {/* Modal de bloqueio */}
      {mostrarBloqueio && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl transform transition-all">
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Cadastro em Análise</h2>
              <p className="text-gray-600 leading-relaxed">
                Seu acesso será liberado após aprovação da Central. Você receberá uma notificação quando for aprovado.
              </p>
            </div>
            <div className="flex gap-3 mt-8">
              <button
                onClick={handleLogout}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-xl font-semibold hover:from-gray-200 hover:to-gray-300 transition-all shadow-md"
              >
                Voltar ao Login
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex">
        {/* Menu Lateral - Sempre Visível */}
        <div className="w-80 bg-white/95 backdrop-blur-xl shadow-2xl border-r border-gray-200/50 flex-shrink-0">
          <div className="p-6 bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Menu</h2>
            </div>
            <div className="flex flex-col items-center">
              {empresaFoto ? (
                <img
                  src={empresaFoto}
                  alt={empresaNome}
                  className="w-20 h-20 rounded-2xl border-4 border-white/30 shadow-xl mb-3 object-cover"
                />
              ) : (
                <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm border-4 border-white/30 shadow-xl mb-3 flex items-center justify-center">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
              )}
              <p className="text-lg font-bold">{empresaNome}</p>
              <p className="text-sm text-blue-100 mt-1">Painel Administrativo</p>
            </div>
          </div>

          <nav className="p-4 space-y-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
            <button
              onClick={() => navigate('/status-pedido')}
              className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <span className="font-semibold text-gray-700">Pedidos</span>
            </button>

            <button
              onClick={() => navigate('/perfil')}
              className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <span className="font-semibold text-gray-700">Perfil</span>
            </button>

            <button
              onClick={() => navigate('/mensalidades')}
              className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <span className="font-semibold text-gray-700">Mensalidade</span>
            </button>

            <button
              onClick={() => navigate('/clientes')}
              className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <span className="font-semibold text-gray-700">Lista de Clientes</span>
            </button>

            <button
              onClick={() => navigate('/relatorio')}
              className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <span className="font-semibold text-gray-700">Histórico de Entregas</span>
            </button>

            <button
              onClick={() => navigate('/tabela-taxas')}
              className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <span className="font-semibold text-gray-700">Tabela de Taxas</span>
            </button>

            <button
              onClick={() => navigate('/configuracoes')}
              className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-gray-500 to-gray-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <span className="font-semibold text-gray-700">Configurações</span>
            </button>

            <div className="pt-4 mt-4 border-t border-gray-200">
              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </div>
                <span className="font-semibold text-red-600">Sair</span>
              </button>
            </div>
          </nav>
        </div>

        {/* Conteúdo Principal */}
        <div className="flex-1 flex flex-col min-h-screen">
          {/* Header Moderno */}
          <header className="bg-white/80 backdrop-blur-lg border-b border-gray-200/50 shadow-sm sticky top-0 z-40">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="flex items-center justify-between h-20">
                <div className="flex items-center gap-3">
                  {empresaFoto ? (
                    <img
                      src={empresaFoto}
                      alt={empresaNome}
                      className="w-12 h-12 rounded-xl border-2 border-gray-200 shadow-sm object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center border-2 border-gray-200 shadow-sm">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                  )}
                  <div>
                    <h1 className="text-xl font-bold text-gray-900">{empresaNome || 'Empresa'}</h1>
                    <p className="text-sm text-gray-500">Painel de Controle</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => {
                      if (mensagensCentral.length === 0) {
                        alert('Não há mensagens da central no momento.');
                        return;
                      }
                      setShowMensagensModal(true);
                    }}
                    className="relative p-3 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    {mensagensNaoLidas > 0 && (
                      <span className="absolute top-1 right-1 bg-gradient-to-r from-red-500 to-red-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center shadow-lg animate-pulse">
                        {mensagensNaoLidas > 99 ? '99+' : mensagensNaoLidas}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </header>

          {/* Conteúdo principal */}
          <main className="flex-1 max-w-7xl mx-auto px-6 lg:px-8 py-8 w-full">
        {/* Cards de Estatísticas - Design Moderno */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <button
            onClick={() => navigate('/status-pedido')}
            className="bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-lg p-6 border border-blue-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-left w-full cursor-pointer"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-md">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-3 py-1 rounded-full">Hoje</span>
            </div>
            <div className="text-4xl font-bold text-gray-900 mb-1">{estatisticas.total}</div>
            <div className="text-sm font-medium text-gray-600">Total Entregues</div>
            <div className="mt-4 pt-4 border-t border-blue-100">
              <div className="flex items-center text-xs text-gray-500">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                Clique para ver detalhes
              </div>
            </div>
          </button>

          <div className="bg-gradient-to-br from-white to-green-50 rounded-2xl shadow-lg p-6 border border-green-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center justify-between mb-4">
              <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-md">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="text-xs font-semibold text-green-600 bg-green-100 px-3 py-1 rounded-full">Total</span>
            </div>
            <div className="text-4xl font-bold text-gray-900 mb-1">
              R$ {formatarValor(parseFloat(estatisticas.totalGasto))}
            </div>
            <div className="text-sm font-medium text-gray-600">Total Pago</div>
            <div className="mt-4 pt-4 border-t border-green-100">
              <div className="flex items-center text-xs text-gray-500">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Valor acumulado
              </div>
            </div>
          </div>

          <button
            onClick={() => navigate('/status-pedido')}
            className="bg-gradient-to-br from-white to-amber-50 rounded-2xl shadow-lg p-6 border border-amber-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-left w-full cursor-pointer"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center shadow-md">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="text-xs font-semibold text-amber-600 bg-amber-100 px-3 py-1 rounded-full">Aguardando</span>
            </div>
            <div className="text-4xl font-bold text-gray-900 mb-1">{estatisticas.pendente}</div>
            <div className="text-sm font-medium text-gray-600">Pendentes</div>
            <div className="mt-4 pt-4 border-t border-amber-100">
              <div className="flex items-center text-xs text-gray-500">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                Clique para ver detalhes
              </div>
            </div>
          </button>
        </div>

        {/* Card Carteira - Design Premium */}
        {usaCarteira && (
          <div className="bg-gradient-to-br from-indigo-600 via-blue-600 to-purple-600 rounded-2xl shadow-2xl p-8 mb-8 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full -mr-32 -mt-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white opacity-10 rounded-full -ml-24 -mb-24"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-blue-100 text-sm font-medium mb-2">Saldo Disponível</p>
                  <div className="flex items-center gap-3">
                    <h2 className="text-5xl font-bold">
                      {saldoVisivel ? `R$ ${formatarValor(saldoCarteira)}` : '••••••'}
                    </h2>
                    <button
                      onClick={() => setSaldoVisivel(!saldoVisivel)}
                      className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                    >
                      {saldoVisivel ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/30">
                  <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowAdicionarSaldo(true)}
                  className="flex-1 bg-white text-blue-600 py-3 rounded-xl font-bold hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  + Adicionar Saldo
                </button>
                <button
                  onClick={() => navigate('/transacoes')}
                  className="px-6 py-3 bg-white/20 backdrop-blur-sm text-white rounded-xl font-semibold hover:bg-white/30 transition-all border border-white/30"
                >
                  Ver Transações
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Botões Principais - Design Moderno */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <button
            onClick={() => {
              setTipoChamado('detalhado');
              setShowChamadoPanel(true);
            }}
            className="group bg-gradient-to-br from-blue-600 to-blue-700 text-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
            <div className="relative z-10">
              <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Chamado Detalhado</h3>
              <p className="text-blue-100 text-sm">Crie entregas completas com todos os detalhes</p>
            </div>
          </button>

          <button
            onClick={() => {
              setTipoChamado('simplificado');
              setShowChamadoPanel(true);
            }}
            className="group bg-gradient-to-br from-indigo-600 to-indigo-700 text-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
            <div className="relative z-10">
              <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Chamado Simplificado</h3>
              <p className="text-indigo-100 text-sm">Criação rápida para entregas conhecidas</p>
            </div>
          </button>

          <button
            onClick={() => navigate('/lancar-entregas')}
            className="group bg-gradient-to-br from-purple-600 to-purple-700 text-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
            <div className="relative z-10">
              <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform relative">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                {solicitacoesPendentes > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-7 h-7 flex items-center justify-center shadow-lg animate-pulse">
                    {solicitacoesPendentes > 99 ? '99+' : solicitacoesPendentes}
                  </span>
                )}
              </div>
              <h3 className="text-xl font-bold mb-2">Lançar Entregas</h3>
              <p className="text-purple-100 text-sm">Registre entregas já realizadas</p>
            </div>
          </button>
        </div>
      </main>
        </div>

      {/* Modal Adicionar Saldo - Design Moderno */}
      {showAdicionarSaldo && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all">
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Adicionar Saldo</h2>
                <button
                  onClick={() => {
                    setShowAdicionarSaldo(false);
                    setValorAdicionar('');
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Valor</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-semibold">R$</span>
                  <input
                    type="text"
                    value={valorAdicionar}
                    onChange={(e) => setValorAdicionar(e.target.value)}
                    placeholder="0,00"
                    className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-lg font-semibold"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowAdicionarSaldo(false);
                    setValorAdicionar('');
                  }}
                  className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAdicionarSaldo}
                  disabled={isLoadingSaldo}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 transition-all shadow-lg hover:shadow-xl"
                >
                  {isLoadingSaldo ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processando...
                    </span>
                  ) : (
                    'Confirmar'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Mensagens Central - Design Moderno */}
      {showMensagensModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="p-6 bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold">Mensagens da Central</h2>
                  {mensagensNaoLidas > 0 && (
                    <p className="text-sm text-blue-100">{mensagensNaoLidas} não lida(s)</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowMensagensModal(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {mensagensCentral.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                  </div>
                  <p className="text-gray-500 text-lg">Não há mensagens no momento.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {mensagensCentral.map((msg: any) => (
                    <div
                      key={msg.id}
                      className={`p-6 rounded-xl border-2 transition-all ${
                        !msg.lida && !msg.fechada 
                          ? 'bg-gradient-to-br from-red-50 to-pink-50 border-red-200 shadow-lg' 
                          : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-md'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <h3 className={`text-lg font-bold ${!msg.lida && !msg.fechada ? 'text-red-700' : 'text-gray-900'}`}>
                          {msg.titulo}
                        </h3>
                        {!msg.lida && !msg.fechada && (
                          <span className="px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-full">Nova</span>
                        )}
                      </div>
                      <p className="text-gray-700 mb-4 leading-relaxed">{msg.corpo}</p>
                      {msg.criadoEm && (
                        <div className="flex items-center text-sm text-gray-500">
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {format(msg.criadoEm.toDate(), "dd/MM/yyyy 'às' HH:mm")}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Painel Lateral de Chamados */}
      {showChamadoPanel && tipoChamado && (
        <ChamadoPanel
          tipo={tipoChamado}
          empresaId={empresaId}
          empresaNome={empresaNome}
          userId={userId || ''}
          usaCarteira={usaCarteira}
          saldoCarteira={saldoCarteira}
          onClose={() => {
            setShowChamadoPanel(false);
            setTipoChamado(null);
          }}
          onSuccess={() => {
            setShowChamadoPanel(false);
            setTipoChamado(null);
            handleRefresh();
          }}
        />
      )}

        {/* Botão Flutuante Chat IA - Design Moderno */}
        <button
          onClick={() => navigate('/chat-ia')}
          className="fixed bottom-24 right-8 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 text-white w-16 h-16 rounded-2xl shadow-2xl hover:shadow-3xl flex items-center justify-center z-30 group transition-all duration-300 hover:scale-110 hover:rotate-3"
          title="Assistente IA"
        >
          <svg className="w-7 h-7 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <div className="absolute -top-2 -right-2 w-4 h-4 bg-green-400 rounded-full border-2 border-white animate-pulse"></div>
        </button>
      </div>
    </>
  );
};

// Componente do Painel Lateral de Chamados
interface ChamadoPanelProps {
  tipo: 'detalhado' | 'simplificado';
  empresaId: string;
  empresaNome: string;
  userId: string;
  usaCarteira: boolean;
  saldoCarteira: number;
  onClose: () => void;
  onSuccess: () => void;
}

interface Setor {
  nome: string;
  valor: number;
}

interface Entrega {
  nomeCliente: string;
  telefone: string;
  endereco: {
    rua: string;
    numero: string;
  };
  setor: string;
  adicional: string;
  tempoProgramado?: number;
  formaPagamento?: string;
  detalhesProduto?: string;
  valorReceber?: string;
  apenasEntrega?: boolean;
  contemBebidas?: boolean;
  paradaBuscarCondicional?: boolean;
}

interface EntregaRapida {
  nomeCliente: string;
  setor: string;
  adicional: string;
  tempoProgramado?: number;
  formaPagamento?: string;
  detalhesProduto?: string;
  valorReceber?: string;
  apenasEntrega?: boolean;
  contemBebidas?: boolean;
  paradaBuscarCondicional?: boolean;
}

const ChamadoPanel: React.FC<ChamadoPanelProps> = ({
  tipo,
  empresaId,
  empresaNome,
  userId,
  usaCarteira,
  saldoCarteira,
  onClose,
  onSuccess
}) => {
  const [entregas, setEntregas] = useState<Entrega[]>([{
    nomeCliente: '',
    telefone: '',
    endereco: { rua: '', numero: '' },
    setor: '',
    adicional: '',
    tempoProgramado: 0,
    formaPagamento: '',
    detalhesProduto: '',
    valorReceber: '',
    apenasEntrega: false,
    contemBebidas: false,
    paradaBuscarCondicional: false
  }]);
  const [entregasRapidas, setEntregasRapidas] = useState<EntregaRapida[]>([{
    nomeCliente: '',
    setor: '',
    adicional: '',
    tempoProgramado: 0,
    formaPagamento: '',
    detalhesProduto: '',
    valorReceber: '',
    apenasEntrega: false,
    contemBebidas: false,
    paradaBuscarCondicional: false
  }]);
  const [setores, setSetores] = useState<Setor[]>([]);
  const [setoresFiltrados, setSetoresFiltrados] = useState<Setor[]>([]);
  const [pesquisaSetor, setPesquisaSetor] = useState('');
  const [showClientesDropdown, setShowClientesDropdown] = useState(false);
  const [clientes, setClientes] = useState<any[]>([]);
  const [clientesFiltrados, setClientesFiltrados] = useState<any[]>([]);
  const [pesquisaCliente, setPesquisaCliente] = useState('');
  const [taxaMotoboyAtiva, setTaxaMotoboyAtiva] = useState(false);
  const [taxaMotoboyValor, setTaxaMotoboyValor] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progressoEntregas, setProgressoEntregas] = useState<{
    total: number;
    processadas: number;
    sucesso: number;
    erro: number;
    processando: boolean;
    detalhes: Array<{ index: number; nomeCliente: string; status: 'processando' | 'sucesso' | 'erro'; mensagem?: string }>;
  }>({
    total: 0,
    processadas: 0,
    sucesso: 0,
    erro: 0,
    processando: false,
    detalhes: []
  });
  const [mensalidadesAtraso, setMensalidadesAtraso] = useState<{
    temAtraso: boolean;
    bloqueado: boolean;
  }>({ temAtraso: false, bloqueado: false });
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  const opcoesTempoProgramado = [
    { label: 'Imediato', valor: 0 },
    { label: '10 minutos', valor: 10 },
    { label: '15 minutos', valor: 15 },
    { label: '20 minutos', valor: 20 },
    { label: '30 minutos', valor: 30 },
    { label: '1 hora', valor: 60 },
    { label: '2 horas', valor: 120 },
    { label: '3 horas', valor: 180 },
  ];

  // Função para fechar o painel e resetar progresso
  const handleClose = () => {
    setProgressoEntregas({
      total: 0,
      processadas: 0,
      sucesso: 0,
      erro: 0,
      processando: false,
      detalhes: []
    });
    onClose();
  };

  // Funções de navegação horizontal
  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      const cardWidth = 420 + 24; // largura do card + gap
      scrollContainerRef.current.scrollBy({ left: -cardWidth, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      const cardWidth = 420 + 24; // largura do card + gap
      scrollContainerRef.current.scrollBy({ left: cardWidth, behavior: 'smooth' });
    }
  };

  // Verificar scroll ao montar e quando as entregas mudam
  useEffect(() => {
    // Pequeno delay para garantir que o DOM foi atualizado
    const timer = setTimeout(() => {
      checkScrollButtons();
    }, 100);

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      window.addEventListener('resize', checkScrollButtons);
      
      return () => {
        clearTimeout(timer);
        container.removeEventListener('scroll', checkScrollButtons);
        window.removeEventListener('resize', checkScrollButtons);
      };
    }

    return () => clearTimeout(timer);
  }, [entregas, entregasRapidas]);

  // Carregar dados iniciais
  useEffect(() => {
    const carregarDados = async () => {
      try {
        if (!userId || !empresaId) return;

        const empresaDoc = await getDoc(doc(db, 'Empresa', empresaId));
        if (empresaDoc.exists()) {
          const empresaData = empresaDoc.data();
          
          if (empresaData.tabelaTaxaId) {
            await carregarSetores(empresaData.tabelaTaxaId);
          }

          // Carregar configurações
          try {
            const empresaConfigRef = doc(db, 'Empresa', empresaId, 'config', 'central');
            const empresaConfigSnap = await getDoc(empresaConfigRef);
            if (empresaConfigSnap.exists()) {
              const cfg = empresaConfigSnap.data();
              setTaxaMotoboyAtiva(!!cfg.habilitarTaxaMotoboy);
              setTaxaMotoboyValor(Number(cfg.taxaMotoboyValor || 0));
            }
          } catch (e) {
            console.warn('Erro ao carregar configurações:', e);
          }
        }

        // Verificar mensalidades
        const resultado = await verificarMensalidadesAtraso(empresaId);
        setMensalidadesAtraso(resultado);
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      }
    };
    carregarDados();
  }, [userId, empresaId]);

  const carregarSetores = async (tabelaTaxaId: string) => {
    try {
      const tabelaDoc = await getDoc(doc(db, 'tabelasTaxas', tabelaTaxaId));
      if (tabelaDoc.exists()) {
        const tabelaData = tabelaDoc.data();
        const setoresData = tabelaData.setores || [];
        setSetores(setoresData);
        setSetoresFiltrados(setoresData);
      }
    } catch (error) {
      console.error('Erro ao carregar setores:', error);
    }
  };

  const verificarMensalidadesAtraso = async (empresaId: string) => {
    try {
      const empresaDoc = await getDoc(doc(db, 'Empresa', empresaId));
      if (!empresaDoc.exists()) {
        return { temAtraso: false, bloqueado: false };
      }

      const empresaData = empresaDoc.data();
      
      if (empresaData.cobrancaRecorrenteAtiva && empresaData.cobrancaRecorrenteId) {
        const cobrancaDoc = await getDoc(doc(db, 'Empresa', empresaId, 'cobrancasRecorrentes', empresaData.cobrancaRecorrenteId));
        if (cobrancaDoc.exists()) {
          const cobrancaData = cobrancaDoc.data();
          const parcelas = cobrancaData.parcelas || [];
          const carenciaEmpresa = cobrancaData.carenciaEmpresa || 7;
          
          const hoje = new Date();
          const parcelasAtrasadas = parcelas.filter((parcela: any) => {
            if (parcela.status === 'paga') return false;
            const dataVencimento = parcela.dataVencimento?.toDate ? parcela.dataVencimento.toDate() : new Date(parcela.dataVencimento);
            return dataVencimento < hoje;
          });

          let bloqueado = false;
          if (parcelasAtrasadas.length > 0) {
            const parcelaMaisAntiga = parcelasAtrasadas.reduce((maisAntiga: any, parcela: any) => {
              const dataVencimento = parcela.dataVencimento?.toDate ? parcela.dataVencimento.toDate() : new Date(parcela.dataVencimento);
              const dataMaisAntiga = maisAntiga.dataVencimento?.toDate ? maisAntiga.dataVencimento.toDate() : new Date(maisAntiga.dataVencimento);
              return dataVencimento < dataMaisAntiga ? parcela : maisAntiga;
            });

            const dataVencimentoMaisAntiga = parcelaMaisAntiga.dataVencimento?.toDate ? parcelaMaisAntiga.dataVencimento.toDate() : new Date(parcelaMaisAntiga.dataVencimento);
            const diasEmAtraso = Math.floor((hoje.getTime() - dataVencimentoMaisAntiga.getTime()) / (1000 * 60 * 60 * 24));
            bloqueado = diasEmAtraso > carenciaEmpresa;
          }

          return {
            temAtraso: parcelasAtrasadas.length > 0,
            bloqueado: bloqueado
          };
        }
      }

      return { temAtraso: false, bloqueado: false };
    } catch (error) {
      console.error('Erro ao verificar mensalidades:', error);
      return { temAtraso: false, bloqueado: false };
    }
  };

  // Carregar clientes
  useEffect(() => {
    const carregarClientes = async () => {
      if (!userId) return;
      try {
        const clientesRef = collection(db, 'usuarios', userId, 'clientes');
        const snapshot = await getDocs(clientesRef);
        const clientesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setClientes(clientesData);
      } catch (error) {
        console.error('Erro ao carregar clientes:', error);
      }
    };
    carregarClientes();
  }, [userId]);

  // Filtrar setores
  useEffect(() => {
    if (pesquisaSetor.trim() === '') {
      setSetoresFiltrados(setores);
    } else {
      const filtrados = setores.filter(setor =>
        setor.nome.toLowerCase().includes(pesquisaSetor.toLowerCase())
      );
      setSetoresFiltrados(filtrados);
    }
  }, [pesquisaSetor, setores]);

  // Filtrar clientes
  useEffect(() => {
    if (pesquisaCliente.trim() === '') {
      setShowClientesDropdown(false);
      setClientesFiltrados([]);
    } else {
      const filtrados = clientes.filter(cliente =>
        cliente.nome?.toLowerCase().includes(pesquisaCliente.toLowerCase()) ||
        cliente.telefone?.includes(pesquisaCliente)
      );
      setClientesFiltrados(filtrados);
      // Mostrar dropdown se houver resultados OU se for detalhado e tiver dados para adicionar
      setShowClientesDropdown(filtrados.length > 0 || (tipo === 'detalhado' && true));
    }
  }, [pesquisaCliente, clientes, tipo]);

  const calcularTotal = () => {
    let total = 0;
    const listaEntregas = tipo === 'detalhado' ? entregas : entregasRapidas;
    listaEntregas.forEach((entrega: any) => {
      const setor = setores.find(s => s.nome === entrega.setor);
      if (setor) {
        const valorSetor = Number(setor.valor || 0);
        const taxa = usaCarteira && taxaMotoboyAtiva ? Number(taxaMotoboyValor || 0) : 0;
        total += valorSetor + taxa;
      }
    });
    return total;
  };

  const handleCriarChamado = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      // Verificar mensalidades em atraso
      if (mensalidadesAtraso.temAtraso && mensalidadesAtraso.bloqueado) {
        alert('Seu acesso foi bloqueado devido a mensalidades em atraso. Regularize suas cobranças recorrentes para voltar a criar chamados.');
        setIsSubmitting(false);
        return;
      }

      if (tipo === 'detalhado') {
        // Validações detalhado
        const entregasSemSetor = entregas.some(e => !e.setor);
        if (entregasSemSetor) {
          alert('Por favor, selecione o setor para todas as entregas.');
          setIsSubmitting(false);
          return;
        }

        const entregasInvalidas = entregas.some(e => !e.nomeCliente || !e.telefone || !e.endereco.rua || !e.endereco.numero);
        if (entregasInvalidas) {
          alert('Preencha todos os campos obrigatórios para todas as entregas.');
          setIsSubmitting(false);
          return;
        }

        // Verificar frete zero
        const entregasComFreteZero = entregas.filter(entrega => {
          const setorObj = setores.find(s => s.nome === entrega.setor);
          const frete = setorObj ? Number(setorObj.valor || 0) : 0;
          return isNaN(frete) || frete <= 0;
        });
        if (entregasComFreteZero.length > 0) {
          alert('Há entregas com frete zerado. Configure os valores da tabela de taxas antes de criar o chamado.');
          setIsSubmitting(false);
          return;
        }

        // Verificar saldo
        const total = calcularTotal();
        if (usaCarteira) {
          const saldoDisponivel = await walletService.getAvailableBalance(empresaId);
          if (saldoDisponivel < total) {
            alert(`Saldo insuficiente. Necessário: R$ ${total.toFixed(2).replace('.', ',')}, Disponível: R$ ${saldoDisponivel.toFixed(2).replace('.', ',')}`);
            setIsSubmitting(false);
            return;
          }
        }

        // Separar entregas imediatas e agendadas
        const entregasImediatas = entregas.filter(e => !e.tempoProgramado || e.tempoProgramado === 0);
        const entregasAgendadas = entregas.filter(e => e.tempoProgramado && e.tempoProgramado > 0);

        // Criar chamados imediatos
        if (entregasImediatas.length > 0) {
          const totalEntregas = entregasImediatas.length + entregasAgendadas.length;
          
          // Só mostrar progresso se houver múltiplas entregas
          if (totalEntregas > 1) {
            setProgressoEntregas({
              total: totalEntregas,
              processadas: 0,
              sucesso: 0,
              erro: 0,
              processando: true,
              detalhes: entregasImediatas.map((e, idx) => ({
                index: idx,
                nomeCliente: e.nomeCliente,
                status: 'processando' as const,
                mensagem: 'Processando...'
              }))
            });
          }

          const entregasFormatadas = entregasImediatas.map((e, idx) => ({
            nomeCliente: e.nomeCliente,
            telefone: e.telefone,
            endereco: e.endereco,
            setor: e.setor,
            adicional: e.adicional,
            tempoProgramado: e.tempoProgramado || 0,
            formaPagamento: e.formaPagamento || '',
            detalhesProduto: e.detalhesProduto || '',
            valorReceber: e.valorReceber || '',
            apenasEntrega: e.apenasEntrega || false,
            contemBebidas: e.contemBebidas || false,
            paradaBuscarCondicional: e.paradaBuscarCondicional || false
          }));

          try {
            const response = await fetchWithAuth(
              'https://us-central1-vs-sistemadeentregas.cloudfunctions.net/criarChamadosEmGrupo',
              {
                method: 'POST',
                wrapAsCallableData: true,
                body: {
                  entregas: entregasFormatadas,
                  empresaId,
                  empresaNome,
                  usaCarteira,
                  taxaMotoboyAtiva,
                  taxaMotoboyValor,
                  setores,
                  tipo: 'detalhada',
                }
              }
            );

            if (!response.ok) {
              const errorText = await response.text();
              throw new Error(errorText || 'Erro ao criar chamados');
            }

            // Atualizar progresso com sucesso (só se houver múltiplas entregas)
            if (totalEntregas > 1) {
              setProgressoEntregas(prev => ({
                ...prev,
                processadas: entregasImediatas.length,
                sucesso: entregasImediatas.length,
                detalhes: prev.detalhes.map((d, idx) => 
                  idx < entregasImediatas.length 
                    ? { ...d, status: 'sucesso' as const, mensagem: 'Criado com sucesso!' }
                    : d
                )
              }));
            }
          } catch (error: any) {
            if (totalEntregas > 1) {
              setProgressoEntregas(prev => ({
                ...prev,
                processadas: entregasImediatas.length,
                erro: entregasImediatas.length,
                detalhes: prev.detalhes.map((d, idx) => 
                  idx < entregasImediatas.length 
                    ? { ...d, status: 'erro' as const, mensagem: error.message || 'Erro ao criar' }
                    : d
                )
              }));
            }
            throw error;
          }
        }

        // Criar chamados agendados
        if (entregasAgendadas.length > 0) {
          // Adicionar entregas agendadas ao progresso
          setProgressoEntregas(prev => ({
            ...prev,
            detalhes: [
              ...prev.detalhes,
              ...entregasAgendadas.map((e, idx) => ({
                index: prev.detalhes.length + idx,
                nomeCliente: e.nomeCliente,
                status: 'processando' as const,
                mensagem: 'Processando agendamento...'
              }))
            ]
          }));

          const now = Date.now();
          const payloadAgendadas = entregasAgendadas.map(e => ({
            nomeCliente: e.nomeCliente,
            telefone: e.telefone,
            endereco: e.endereco,
            setor: e.setor,
            adicional: e.adicional,
            tempoProgramado: e.tempoProgramado || 0,
            agendada: true,
            tempoProgramadoMin: Number(e.tempoProgramado || 0),
            liberadoEmMs: now + Math.max(0, Number(e.tempoProgramado || 0)) * 60 * 1000,
            formaPagamento: e.formaPagamento || '',
            detalhesProduto: e.detalhesProduto || '',
            valorReceber: e.valorReceber || '',
            apenasEntrega: e.apenasEntrega || false,
            contemBebidas: e.contemBebidas || false,
            paradaBuscarCondicional: e.paradaBuscarCondicional || false
          }));

          try {
            const responseAgendadas = await fetchWithAuth(
              'https://us-central1-vs-sistemadeentregas.cloudfunctions.net/criarChamadosEmGrupo',
              {
                method: 'POST',
                wrapAsCallableData: true,
                body: {
                  entregas: payloadAgendadas,
                  empresaId,
                  empresaNome,
                  usaCarteira,
                  taxaMotoboyAtiva,
                  taxaMotoboyValor,
                  setores,
                  tipo: 'detalhada',
                  propagarPorFila: true
                }
              }
            );

            if (!responseAgendadas.ok) {
              const errorText = await responseAgendadas.text();
              throw new Error(errorText || 'Erro ao criar chamados agendados');
            }

            // Atualizar progresso com sucesso das agendadas (só se houver múltiplas entregas)
            const totalEntregas = entregasImediatas.length + entregasAgendadas.length;
            if (totalEntregas > 1) {
              setProgressoEntregas(prev => ({
                ...prev,
                processadas: prev.total,
                sucesso: prev.sucesso + entregasAgendadas.length,
                detalhes: prev.detalhes.map((d, idx) => 
                  idx >= entregas.length - entregasAgendadas.length
                    ? { ...d, status: 'sucesso' as const, mensagem: 'Agendado com sucesso!' }
                    : d
                )
              }));
            }
          } catch (error: any) {
            const totalEntregas = entregasImediatas.length + entregasAgendadas.length;
            if (totalEntregas > 1) {
              setProgressoEntregas(prev => ({
                ...prev,
                processadas: prev.total,
                erro: prev.erro + entregasAgendadas.length,
                detalhes: prev.detalhes.map((d, idx) => 
                  idx >= entregas.length - entregasAgendadas.length
                    ? { ...d, status: 'erro' as const, mensagem: error.message || 'Erro ao agendar' }
                    : d
                )
              }));
            }
            throw error;
          }
        }
      } else {
        // Validações simplificado
        const entregasSemSetor = entregasRapidas.some(e => !e.setor);
        if (entregasSemSetor) {
          alert('Por favor, selecione o setor para todas as entregas.');
          setIsSubmitting(false);
          return;
        }

        const entregasInvalidas = entregasRapidas.some(e => !e.nomeCliente);
        if (entregasInvalidas) {
          alert('Preencha o nome do cliente para todas as entregas.');
          setIsSubmitting(false);
          return;
        }

        // Verificar frete zero
        const entregasComFreteZero = entregasRapidas.filter(entrega => {
          const setorObj = setores.find(s => s.nome === entrega.setor);
          const frete = setorObj ? Number(setorObj.valor || 0) : 0;
          return isNaN(frete) || frete <= 0;
        });
        if (entregasComFreteZero.length > 0) {
          alert('Há entregas com frete zerado. Configure os valores da tabela de taxas antes de criar o chamado.');
          setIsSubmitting(false);
          return;
        }

        // Verificar saldo
        const total = calcularTotal();
        if (usaCarteira) {
          const saldoDisponivel = await walletService.getAvailableBalance(empresaId);
          if (saldoDisponivel < total) {
            alert(`Saldo insuficiente. Necessário: R$ ${total.toFixed(2).replace('.', ',')}, Disponível: R$ ${saldoDisponivel.toFixed(2).replace('.', ',')}`);
            setIsSubmitting(false);
            return;
          }
        }

        // Separar entregas imediatas e agendadas
        const entregasImediatas = entregasRapidas.filter(e => !e.tempoProgramado || e.tempoProgramado === 0);
        const entregasAgendadas = entregasRapidas.filter(e => e.tempoProgramado && e.tempoProgramado > 0);

        // Criar chamados imediatos
        if (entregasImediatas.length > 0) {
          const totalEntregas = entregasImediatas.length + entregasAgendadas.length;
          
          // Só mostrar progresso se houver múltiplas entregas
          if (totalEntregas > 1) {
            setProgressoEntregas({
              total: totalEntregas,
              processadas: 0,
              sucesso: 0,
              erro: 0,
              processando: true,
              detalhes: entregasImediatas.map((e, idx) => ({
                index: idx,
                nomeCliente: e.nomeCliente,
                status: 'processando' as const,
                mensagem: 'Processando...'
              }))
            });
          }

          const entregasFormatadas = entregasImediatas.map(e => ({
            nomeCliente: e.nomeCliente,
            setor: e.setor,
            adicional: e.adicional,
            tempoProgramado: e.tempoProgramado || 0,
            formaPagamento: e.formaPagamento || '',
            detalhesProduto: e.detalhesProduto || '',
            valorReceber: e.valorReceber || '',
            apenasEntrega: e.apenasEntrega || false,
            contemBebidas: e.contemBebidas || false,
            paradaBuscarCondicional: e.paradaBuscarCondicional || false
          }));

          try {
            const response = await fetchWithAuth(
              'https://us-central1-vs-sistemadeentregas.cloudfunctions.net/criarChamadosEmGrupo',
              {
                method: 'POST',
                wrapAsCallableData: true,
                body: {
                  entregas: entregasFormatadas,
                  empresaId,
                  empresaNome,
                  usaCarteira,
                  taxaMotoboyAtiva,
                  taxaMotoboyValor,
                  setores,
                  tipo: 'simplificada',
                }
              }
            );

            if (!response.ok) {
              const errorText = await response.text();
              throw new Error(errorText || 'Erro ao criar chamados');
            }

            // Atualizar progresso com sucesso (só se houver múltiplas entregas)
            if (totalEntregas > 1) {
              setProgressoEntregas(prev => ({
                ...prev,
                processadas: entregasImediatas.length,
                sucesso: entregasImediatas.length,
                detalhes: prev.detalhes.map((d, idx) => 
                  idx < entregasImediatas.length 
                    ? { ...d, status: 'sucesso' as const, mensagem: 'Criado com sucesso!' }
                    : d
                )
              }));
            }
          } catch (error: any) {
            if (totalEntregas > 1) {
              setProgressoEntregas(prev => ({
                ...prev,
                processadas: entregasImediatas.length,
                erro: entregasImediatas.length,
                detalhes: prev.detalhes.map((d, idx) => 
                  idx < entregasImediatas.length 
                    ? { ...d, status: 'erro' as const, mensagem: error.message || 'Erro ao criar' }
                    : d
                )
              }));
            }
            throw error;
          }
        }

        // Criar chamados agendados
        if (entregasAgendadas.length > 0) {
          const totalEntregas = entregasImediatas.length + entregasAgendadas.length;
          
          // Adicionar entregas agendadas ao progresso (só se houver múltiplas entregas)
          if (totalEntregas > 1) {
            setProgressoEntregas(prev => ({
              ...prev,
              detalhes: [
                ...prev.detalhes,
                ...entregasAgendadas.map((e, idx) => ({
                  index: prev.detalhes.length + idx,
                  nomeCliente: e.nomeCliente,
                  status: 'processando' as const,
                  mensagem: 'Processando agendamento...'
                }))
              ]
            }));
          }

          const now = Date.now();
          const payloadAgendadas = entregasAgendadas.map(e => ({
            nomeCliente: e.nomeCliente,
            setor: e.setor,
            adicional: e.adicional,
            tempoProgramado: e.tempoProgramado || 0,
            agendada: true,
            tempoProgramadoMin: Number(e.tempoProgramado || 0),
            liberadoEmMs: now + Math.max(0, Number(e.tempoProgramado || 0)) * 60 * 1000,
            formaPagamento: e.formaPagamento || '',
            detalhesProduto: e.detalhesProduto || '',
            valorReceber: e.valorReceber || '',
            apenasEntrega: e.apenasEntrega || false,
            contemBebidas: e.contemBebidas || false,
            paradaBuscarCondicional: e.paradaBuscarCondicional || false
          }));

          try {
            const responseAgendadas = await fetchWithAuth(
              'https://us-central1-vs-sistemadeentregas.cloudfunctions.net/criarChamadosEmGrupo',
              {
                method: 'POST',
                wrapAsCallableData: true,
                body: {
                  entregas: payloadAgendadas,
                  empresaId,
                  empresaNome,
                  usaCarteira,
                  taxaMotoboyAtiva,
                  taxaMotoboyValor,
                  setores,
                  tipo: 'simplificada',
                  propagarPorFila: true
                }
              }
            );

            if (!responseAgendadas.ok) {
              const errorText = await responseAgendadas.text();
              throw new Error(errorText || 'Erro ao criar chamados agendados');
            }

            // Atualizar progresso com sucesso das agendadas (só se houver múltiplas entregas)
            const totalEntregas = entregasImediatas.length + entregasAgendadas.length;
            if (totalEntregas > 1) {
              setProgressoEntregas(prev => ({
                ...prev,
                processadas: prev.total,
                sucesso: prev.sucesso + entregasAgendadas.length,
                detalhes: prev.detalhes.map((d, idx) => 
                  idx >= entregasRapidas.length - entregasAgendadas.length
                    ? { ...d, status: 'sucesso' as const, mensagem: 'Agendado com sucesso!' }
                    : d
                )
              }));
            }
          } catch (error: any) {
            const totalEntregas = entregasImediatas.length + entregasAgendadas.length;
            if (totalEntregas > 1) {
              setProgressoEntregas(prev => ({
                ...prev,
                processadas: prev.total,
                erro: prev.erro + entregasAgendadas.length,
                detalhes: prev.detalhes.map((d, idx) => 
                  idx >= entregasRapidas.length - entregasAgendadas.length
                    ? { ...d, status: 'erro' as const, mensagem: error.message || 'Erro ao agendar' }
                    : d
                )
              }));
            }
            throw error;
          }
        }
      }

      // Finalizar progresso
      const totalSucesso = progressoEntregas.sucesso;
      const totalErro = progressoEntregas.erro;
      
      setProgressoEntregas(prev => ({
        ...prev,
        processando: false
      }));

      // Se houver apenas uma entrega, não mostrar modal, apenas alerta
      if (progressoEntregas.total <= 1) {
        alert('Chamado criado com sucesso!');
        setProgressoEntregas({
          total: 0,
          processadas: 0,
          sucesso: 0,
          erro: 0,
          processando: false,
          detalhes: []
        });
        onSuccess();
      } else {
        // Para múltiplas entregas, o modal já mostra o resultado
        // Aguardar usuário fechar o modal antes de chamar onSuccess
        if (totalErro === 0) {
          // Se tudo deu certo, não precisa fazer nada aqui, o botão do modal chama onSuccess
        }
      }
    } catch (error: any) {
      console.error('Erro ao criar chamado:', error);
      
      // Finalizar progresso com erro
      const totalErro = progressoEntregas.erro;
      
      setProgressoEntregas(prev => ({
        ...prev,
        processando: false
      }));

      // Se houver apenas uma entrega, mostrar alerta simples
      if (progressoEntregas.total <= 1) {
        alert(error.message || 'Erro ao criar chamado. Tente novamente.');
        setProgressoEntregas({
          total: 0,
          processadas: 0,
          sucesso: 0,
          erro: 0,
          processando: false,
          detalhes: []
        });
      }
      // Para múltiplas entregas, o modal já mostra os erros
    } finally {
      setIsSubmitting(false);
    }
  };

  // Funções auxiliares para detalhado
  const adicionarEntrega = () => {
    if (tipo === 'detalhado') {
      setEntregas([...entregas, {
        nomeCliente: '',
        telefone: '',
        endereco: { rua: '', numero: '' },
        setor: '',
        adicional: '',
        tempoProgramado: 0,
        formaPagamento: '',
        detalhesProduto: '',
        valorReceber: '',
        apenasEntrega: false,
        contemBebidas: false,
        paradaBuscarCondicional: false
      }]);
    } else {
      setEntregasRapidas([...entregasRapidas, {
        nomeCliente: '',
        setor: '',
        adicional: '',
        tempoProgramado: 0,
        formaPagamento: '',
        detalhesProduto: '',
        valorReceber: '',
        apenasEntrega: false,
        contemBebidas: false,
        paradaBuscarCondicional: false
      }]);
    }
  };

  const removerEntrega = (index: number) => {
    if (tipo === 'detalhado') {
      if (entregas.length > 1) {
        setEntregas(entregas.filter((_, idx) => idx !== index));
      } else {
        alert('É necessário ter pelo menos uma entrega no chamado.');
      }
    } else {
      if (entregasRapidas.length > 1) {
        setEntregasRapidas(entregasRapidas.filter((_, idx) => idx !== index));
      } else {
        alert('É necessário ter pelo menos uma entrega no chamado.');
      }
    }
  };

  const atualizarEntrega = (index: number, campo: any, valor: string | number | boolean) => {
    if (tipo === 'detalhado') {
      const novasEntregas = [...entregas];
      if (campo === 'endereco.rua') {
        novasEntregas[index].endereco.rua = valor as string;
      } else if (campo === 'endereco.numero') {
        novasEntregas[index].endereco.numero = valor as string;
      } else if (campo === 'tempoProgramado') {
        novasEntregas[index].tempoProgramado = valor as number;
      } else {
        novasEntregas[index][campo] = valor;
      }
      setEntregas(novasEntregas);
    } else {
      const novasEntregas = [...entregasRapidas];
      if (campo === 'tempoProgramado') {
        novasEntregas[index].tempoProgramado = valor as number;
      } else {
        novasEntregas[index][campo] = valor;
      }
      setEntregasRapidas(novasEntregas);
    }
  };

  const selecionarCliente = (cliente: any, index: number) => {
    const enderecos = cliente.enderecos && Array.isArray(cliente.enderecos)
      ? cliente.enderecos
      : (cliente.endereco ? [cliente.endereco] : []);

    if (tipo === 'detalhado') {
      if (enderecos.length > 0) {
        const endereco = enderecos[0];
        atualizarEntrega(index, 'nomeCliente', cliente.nome || '');
        atualizarEntrega(index, 'telefone', cliente.telefone || '');
        atualizarEntrega(index, 'endereco.rua', endereco.rua || '');
        atualizarEntrega(index, 'endereco.numero', endereco.numero || '');
        atualizarEntrega(index, 'adicional', endereco.adicional || '');
        atualizarEntrega(index, 'setor', endereco.setor || '');
      } else {
        atualizarEntrega(index, 'nomeCliente', cliente.nome || '');
        atualizarEntrega(index, 'telefone', cliente.telefone || '');
      }
    } else {
      if (enderecos.length > 0) {
        const endereco = enderecos[0];
        atualizarEntrega(index, 'nomeCliente', cliente.nome || '');
        atualizarEntrega(index, 'setor', endereco.setor || '');
        atualizarEntrega(index, 'adicional', endereco.adicional || '');
      } else {
        atualizarEntrega(index, 'nomeCliente', cliente.nome || '');
      }
    }
    setPesquisaCliente('');
    setShowClientesDropdown(false);
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 transition-opacity"
        onClick={handleClose}
      />
      <div className="fixed inset-y-0 right-0 w-full max-w-4xl bg-white shadow-2xl z-50 overflow-y-auto transform transition-transform">
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 flex items-center justify-between z-10">
          <div>
            <h2 className="text-2xl font-bold">
              {tipo === 'detalhado' ? 'Chamado Detalhado' : 'Chamado Simplificado'}
            </h2>
            <p className="text-blue-100 text-sm mt-1">
              {tipo === 'detalhado' ? 'Crie entregas completas' : 'Criação rápida para entregas conhecidas'}
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting && progressoEntregas.processando}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {/* Card de Informações */}
          <div className="bg-white rounded-xl shadow-lg p-4 mb-4 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-1">Informações da Entrega</h3>
                <div className="text-xs text-gray-600">
                  Total estimado: <span className="font-bold text-gray-900">R$ {calcularTotal().toFixed(2).replace('.', ',')}</span>
                </div>
              </div>
              {usaCarteira && (
                <div className="text-xs text-right">
                  <div className="text-gray-500">Saldo</div>
                  <div className="font-bold text-blue-600">R$ {saldoCarteira.toFixed(2).replace('.', ',')}</div>
                </div>
              )}
            </div>
          </div>

          {/* Lista de Entregas - Layout Horizontal */}
          <div className="mb-6 relative">
            {/* Seta Esquerda */}
            {(tipo === 'detalhado' ? entregas : entregasRapidas).length > 1 && canScrollLeft && (
              <button
                onClick={scrollLeft}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 backdrop-blur-sm border-2 border-gray-200 rounded-full p-2 shadow-lg hover:bg-white hover:border-blue-500 transition-all group"
                style={{ marginLeft: '-12px' }}
              >
                <svg className="w-6 h-6 text-gray-700 group-hover:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}

            {/* Seta Direita */}
            {(tipo === 'detalhado' ? entregas : entregasRapidas).length > 1 && canScrollRight && (
              <button
                onClick={scrollRight}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 backdrop-blur-sm border-2 border-gray-200 rounded-full p-2 shadow-lg hover:bg-white hover:border-blue-500 transition-all group"
                style={{ marginRight: '-12px' }}
              >
                <svg className="w-6 h-6 text-gray-700 group-hover:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}

            <div 
              ref={scrollContainerRef}
              className="flex gap-6 overflow-x-auto pb-4 -mx-6 px-6 snap-x snap-mandatory"
              style={{ 
                scrollbarWidth: 'thin', 
                scrollbarColor: '#93c5fd #f3f4f6',
                WebkitOverflowScrolling: 'touch'
              }}
            >
              {(tipo === 'detalhado' ? entregas : entregasRapidas).map((entrega: any, index: number) => {
                // Cores alternadas para destacar cada card
                const coresFundo = [
                  'bg-gradient-to-br from-blue-50 to-blue-100/50',
                  'bg-gradient-to-br from-indigo-50 to-indigo-100/50',
                  'bg-gradient-to-br from-purple-50 to-purple-100/50',
                  'bg-gradient-to-br from-pink-50 to-pink-100/50',
                  'bg-gradient-to-br from-rose-50 to-rose-100/50',
                  'bg-gradient-to-br from-orange-50 to-orange-100/50',
                  'bg-gradient-to-br from-amber-50 to-amber-100/50',
                  'bg-gradient-to-br from-yellow-50 to-yellow-100/50',
                  'bg-gradient-to-br from-lime-50 to-lime-100/50',
                  'bg-gradient-to-br from-emerald-50 to-emerald-100/50',
                ];
                const corFundo = coresFundo[index % coresFundo.length];
                
                return (
                <div key={index} className={`${corFundo} rounded-xl shadow-lg p-4 border-2 border-white/50 flex-shrink-0 w-full min-w-[420px] max-w-[420px] snap-start backdrop-blur-sm`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-base font-semibold text-gray-900">Entrega {index + 1}</h3>
                  {(tipo === 'detalhado' ? entregas : entregasRapidas).length > 1 && (
                    <button
                      onClick={() => removerEntrega(index)}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>

                <div className="space-y-2">
                  {/* Nome do Cliente */}
                  <div className="relative">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Nome do Cliente *</label>
                    <input
                      type="text"
                      value={entrega.nomeCliente}
                      onChange={(e) => {
                        atualizarEntrega(index, 'nomeCliente', e.target.value);
                        setPesquisaCliente(e.target.value);
                        if (e.target.value) {
                          setShowClientesDropdown(true);
                        } else {
                          setShowClientesDropdown(false);
                        }
                      }}
                      onFocus={() => {
                        if (entrega.nomeCliente) {
                          setPesquisaCliente(entrega.nomeCliente);
                          const filtrados = clientes.filter(cliente =>
                            cliente.nome?.toLowerCase().includes(entrega.nomeCliente.toLowerCase()) ||
                            cliente.telefone?.includes(entrega.nomeCliente)
                          );
                          setClientesFiltrados(filtrados);
                          if (filtrados.length > 0) {
                            setShowClientesDropdown(true);
                          }
                        }
                      }}
                      onBlur={() => {
                        setTimeout(() => setShowClientesDropdown(false), 200);
                      }}
                      placeholder="Buscar cliente ou digitar nome"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-all text-gray-900"
                    />
                    {((showClientesDropdown && clientesFiltrados.length > 0) || (pesquisaCliente && pesquisaCliente.trim() !== '' && clientesFiltrados.length === 0 && tipo === 'detalhado' && entrega.nomeCliente.trim() !== '' && entrega.telefone.trim() !== '' && entrega.setor.trim() !== '')) && (
                      <div 
                        className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl max-h-48 overflow-y-auto"
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        {clientesFiltrados.length > 0 ? (
                          clientesFiltrados.map((cliente) => (
                            <button
                              key={cliente.id}
                              onClick={() => selecionarCliente(cliente, index)}
                              className="w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-0 text-sm"
                            >
                              <div className="font-semibold text-gray-900">{cliente.nome}</div>
                              {cliente.telefone && (
                                <div className="text-xs text-gray-600">{cliente.telefone}</div>
                              )}
                            </button>
                          ))
                        ) : (
                          tipo === 'detalhado' && entrega.nomeCliente.trim() !== '' && entrega.telefone.trim() !== '' && entrega.setor.trim() !== '' && (
                            <button
                              onClick={async () => {
                                try {
                                  if (!userId) return;
                                  
                                  const novoCliente = {
                                    nome: entrega.nomeCliente,
                                    telefone: entrega.telefone,
                                    endereco: {
                                      rua: entrega.endereco.rua,
                                      numero: entrega.endereco.numero,
                                      adicional: entrega.adicional || '',
                                      setor: entrega.setor || ''
                                    },
                                    criadoEm: serverTimestamp()
                                  };

                                  const clientesRef = collection(db, 'usuarios', userId, 'clientes');
                                  await addDoc(clientesRef, novoCliente);
                                  
                                  // Recarregar clientes
                                  const snapshot = await getDocs(clientesRef);
                                  const clientesData = snapshot.docs.map(doc => ({
                                    id: doc.id,
                                    ...doc.data()
                                  }));
                                  setClientes(clientesData);
                                  
                                  alert('Cliente adicionado à lista com sucesso!');
                                  setPesquisaCliente('');
                                  setShowClientesDropdown(false);
                                } catch (error) {
                                  console.error('Erro ao adicionar cliente:', error);
                                  alert('Erro ao adicionar cliente. Tente novamente.');
                                }
                              }}
                              className="w-full text-left px-3 py-2 hover:bg-green-50 transition-colors border-b border-gray-100 text-sm bg-green-50/50"
                            >
                              <div className="flex items-center gap-2">
                                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                <div>
                                  <div className="font-semibold text-gray-900">Adicionar "{entrega.nomeCliente}" à lista</div>
                                  <div className="text-xs text-gray-600">Cliente será salvo com os dados preenchidos</div>
                                </div>
                              </div>
                            </button>
                          )
                        )}
                      </div>
                    )}
                  </div>

                  {/* Campos específicos do detalhado */}
                  {tipo === 'detalhado' && (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Telefone *</label>
                        <input
                          type="tel"
                          value={entrega.telefone}
                          onChange={(e) => atualizarEntrega(index, 'telefone', e.target.value)}
                          placeholder="(00) 00000-0000"
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-all text-gray-900"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Rua *</label>
                          <input
                            type="text"
                            value={entrega.endereco.rua}
                            onChange={(e) => atualizarEntrega(index, 'endereco.rua', e.target.value)}
                            placeholder="Nome da rua"
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-all text-gray-900"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Número *</label>
                          <input
                            type="text"
                            value={entrega.endereco.numero}
                            onChange={(e) => atualizarEntrega(index, 'endereco.numero', e.target.value)}
                            placeholder="Número"
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-all text-gray-900"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Setor */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Setor *</label>
                    <div className="relative">
                      <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                          <input
                            type="text"
                            value={pesquisaSetor}
                            onChange={(e) => setPesquisaSetor(e.target.value)}
                            placeholder={entrega.setor || "Buscar setor"}
                            className="w-full px-3 py-2 pr-20 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-all text-gray-900"
                          />
                          {entrega.setor && !pesquisaSetor && (() => {
                            const setorSelecionado = setores.find(s => s.nome === entrega.setor);
                            if (setorSelecionado) {
                              const taxa = usaCarteira && taxaMotoboyAtiva ? Number(taxaMotoboyValor || 0) : 0;
                              const valorFinal = Number(setorSelecionado.valor || 0) + taxa;
                              return (
                                <div className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-blue-50 border border-blue-200 rounded-md">
                                  <span className="text-xs font-bold text-blue-700">R$ {valorFinal.toFixed(2).replace('.', ',')}</span>
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </div>
                      {pesquisaSetor && setoresFiltrados.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                          {setoresFiltrados.map((setor, idx) => {
                            const taxa = usaCarteira && taxaMotoboyAtiva ? Number(taxaMotoboyValor || 0) : 0;
                            const valorFinal = Number(setor.valor || 0) + taxa;
                            return (
                              <button
                                key={idx}
                                onClick={() => {
                                  atualizarEntrega(index, 'setor', setor.nome);
                                  setPesquisaSetor('');
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-0 text-sm"
                              >
                                <div className="font-semibold text-gray-900">{setor.nome}</div>
                                <div className="text-xs text-blue-600 font-medium">R$ {valorFinal.toFixed(2).replace('.', ',')}</div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Adicional */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Observações Adicionais</label>
                    <textarea
                      value={entrega.adicional}
                      onChange={(e) => atualizarEntrega(index, 'adicional', e.target.value)}
                      placeholder="Complemento, referência, etc."
                      rows={2}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-all resize-none text-gray-900"
                    />
                  </div>

                  {/* Tempo Programado */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Tempo Programado</label>
                    <select
                      value={entrega.tempoProgramado || 0}
                      onChange={(e) => atualizarEntrega(index, 'tempoProgramado', Number(e.target.value))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-all text-gray-900"
                    >
                      {opcoesTempoProgramado.map(opcao => (
                        <option key={opcao.valor} value={opcao.valor}>{opcao.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Detalhes Adicionais - Dentro de cada entrega */}
                  <div className="pt-2 border-t border-gray-200 mt-2">
                    <div className="space-y-2">
                      {/* Campos apenas para chamado detalhado */}
                      {tipo === 'detalhado' && (
                        <>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Forma de Pagamento</label>
                            <input
                              type="text"
                              value={entrega.formaPagamento || ''}
                              onChange={(e) => atualizarEntrega(index, 'formaPagamento', e.target.value)}
                              placeholder="Dinheiro, Cartão, PIX, etc."
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-all text-gray-900"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Detalhes do Produto</label>
                            <textarea
                              value={entrega.detalhesProduto || ''}
                              onChange={(e) => atualizarEntrega(index, 'detalhesProduto', e.target.value)}
                              placeholder="Descrição dos produtos"
                              rows={2}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-all resize-none text-gray-900"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Valor a Receber</label>
                            <input
                              type="text"
                              value={entrega.valorReceber || ''}
                              onChange={(e) => atualizarEntrega(index, 'valorReceber', e.target.value)}
                              placeholder="R$ 0,00"
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-all text-gray-900"
                            />
                          </div>
                        </>
                      )}

                      {/* Checkboxes para ambos os tipos */}
                      <div className="space-y-1.5">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={entrega.apenasEntrega || false}
                            onChange={(e) => atualizarEntrega(index, 'apenasEntrega', e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span className="text-xs font-medium text-gray-700">Apenas Entrega</span>
                        </label>

                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={entrega.contemBebidas || false}
                            onChange={(e) => atualizarEntrega(index, 'contemBebidas', e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span className="text-xs font-medium text-gray-700">Contém Bebidas</span>
                        </label>

                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={entrega.paradaBuscarCondicional || false}
                            onChange={(e) => atualizarEntrega(index, 'paradaBuscarCondicional', e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span className="text-xs font-medium text-gray-700">Parada para Buscar (Condicional)</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
                );
              })}
              
              {/* Botão Adicionar Entrega - Dentro do Scroll Horizontal */}
              <button
                onClick={adicionarEntrega}
                className="flex-shrink-0 w-full min-w-[200px] max-w-[200px] h-auto border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50 transition-all font-semibold flex flex-col items-center justify-center p-4 snap-start"
              >
                <svg className="w-8 h-8 mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="text-center text-sm">Adicionar<br />Entrega</span>
              </button>
            </div>
          </div>


          {/* Botões de Ação */}
          <div className="flex gap-4 sticky bottom-0 bg-white pt-4 pb-6 border-t border-gray-200">
            <button
              onClick={handleClose}
              disabled={isSubmitting && progressoEntregas.processando}
              className="flex-1 px-6 py-4 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancelar
            </button>
            <button
              onClick={handleCriarChamado}
              disabled={isSubmitting}
              className={`flex-1 px-6 py-4 text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed ${
                tipo === 'detalhado'
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
                  : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700'
              }`}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Criando...
                </span>
              ) : (
                'Criar Chamado'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Modal de Progresso - Mostra quando há múltiplas entregas */}
      {progressoEntregas.processando && progressoEntregas.total > 1 && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
              <h3 className="text-2xl font-bold">Processando Entregas</h3>
              <p className="text-blue-100 mt-1">
                Criando {progressoEntregas.total} chamado(s)...
              </p>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {/* Barra de Progresso Geral */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Progresso Geral
                  </span>
                  <span className="text-sm font-bold text-blue-600">
                    {progressoEntregas.processadas} / {progressoEntregas.total}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-indigo-600 h-full rounded-full transition-all duration-300"
                    style={{
                      width: `${(progressoEntregas.processadas / progressoEntregas.total) * 100}%`
                    }}
                  />
                </div>
                <div className="flex items-center justify-between mt-2 text-xs text-gray-600">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    {progressoEntregas.sucesso} sucesso
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                    {progressoEntregas.erro} erro
                  </span>
                </div>
              </div>

              {/* Lista de Entregas */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                  Detalhes das Entregas
                </h4>
                {progressoEntregas.detalhes.map((detalhe, idx) => (
                  <div
                    key={idx}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      detalhe.status === 'sucesso'
                        ? 'bg-green-50 border-green-200'
                        : detalhe.status === 'erro'
                        ? 'bg-red-50 border-red-200'
                        : 'bg-blue-50 border-blue-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                            detalhe.status === 'sucesso'
                              ? 'bg-green-500 text-white'
                              : detalhe.status === 'erro'
                              ? 'bg-red-500 text-white'
                              : 'bg-blue-500 text-white animate-pulse'
                          }`}
                        >
                          {detalhe.status === 'sucesso' ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : detalhe.status === 'erro' ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.5m13 0V4M4 19v-5h.5m13 0v5M4 12h16" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">
                            {detalhe.nomeCliente || `Entrega ${detalhe.index + 1}`}
                          </div>
                          <div
                            className={`text-xs mt-1 ${
                              detalhe.status === 'sucesso'
                                ? 'text-green-700'
                                : detalhe.status === 'erro'
                                ? 'text-red-700'
                                : 'text-blue-700'
                            }`}
                          >
                            {detalhe.mensagem}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {!progressoEntregas.processando && (
              <div className="p-6 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => {
                    setProgressoEntregas({
                      total: 0,
                      processadas: 0,
                      sucesso: 0,
                      erro: 0,
                      processando: false,
                      detalhes: []
                    });
                    if (progressoEntregas.erro === 0) {
                      onSuccess();
                    }
                  }}
                  className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl"
                >
                  {progressoEntregas.erro === 0 ? 'Fechar' : 'Tentar Novamente'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default EmpresaHome;
